import { useCallback, useMemo, useState } from 'react'

import type { ComboboxOption } from '@/components/ui/combobox'
import type { WarehousePositionLotType } from '@/lib/api/warehouse-position-api'
import { Combobox } from '@/components/ui/combobox'
import { useDebounced } from '@/hooks/use-debounced'
import { useWarehousePositions } from '@/hooks/use-warehouse-position-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    lotType: WarehousePositionLotType
    value: string | undefined
    onChange: (id: string) => void
    /** Placeholder khi chưa chọn. Mặc định "Vị trí lưu kho" (form nhập lô). */
    placeholder?: string
    /** Cho phép xoá lựa chọn (dùng ở filter danh sách → về "tất cả vị trí"). */
    allowClear?: boolean
    /** Nhãn hiển thị khi `value` nằm ngoài 50 vị trí active tải về (vd sửa lô có
     *  vị trí cũ). Thiếu nó thì trigger tụt về placeholder dù đã có giá trị. */
    fallbackLabel?: string
}

export function PositionPicker({
    lotType,
    value,
    onChange,
    placeholder,
    allowClear,
    fallbackLabel,
}: Props) {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounced(search, 300)

    const { data } = useWarehousePositions({
        lot_type: lotType,
        is_active: true,
        search: debouncedSearch || undefined,
        per_page: 50,
    })

    const options = useMemo(
        () =>
            (data?.data ?? []).map((p) => ({
                value: p.id,
                label: p.name ? `${p.code} — ${p.name}` : p.code,
            })),
        [data],
    )

    // Vị trí đã chọn tay được cache để không rớt khỏi trigger khi search dùng
    // chung đổi (server chỉ trả 50 dòng theo từ khoá hiện tại).
    const [picked, setPicked] = useState<Map<string, ComboboxOption>>(new Map())
    const handleChange = useCallback(
        (id: string) => {
            const opt = options.find((o) => o.value === id)
            if (opt)
                setPicked((prev) => {
                    if (prev.has(id)) return prev
                    const next = new Map(prev)
                    next.set(id, opt)
                    return next
                })
            onChange(id)
        },
        [options, onChange],
    )

    const selectedOption = value
        ? (picked.get(value) ??
          (fallbackLabel ? { value, label: fallbackLabel } : undefined))
        : undefined

    // Ô tìm kiếm nằm trong popover bị Radix unmount khi đóng; reset state search
    // theo, nếu không mở lại thấy ô rỗng mà danh sách vẫn lọc theo từ khoá cũ.
    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) setSearch('')
    }, [])

    return (
        <Combobox
            options={options}
            value={value ?? ''}
            onChange={handleChange}
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={placeholder ?? m.lot_position_label()}
            allowClear={allowClear}
            selectedOption={selectedOption}
            onOpenChange={handleOpenChange}
        />
    )
}
